import { useState, useMemo } from "react";
// @ts-ignore
import { SelectInput } from "@viuti/recursos";
import styles from "./ChecklistModal.module.css";

interface ChecklistItem {
  id: number;
  label: string;
}

interface ChecklistGroup {
  groupLabel: string;
  items: ChecklistItem[];
}

interface ChecklistModalProps {
  title: string;
  items: ChecklistItem[];
  groups?: ChecklistGroup[];
  selectedIds: number[];
  onConfirm?: (selectedIds: number[]) => void;
  onClose: () => void;
  readOnly?: boolean;
  headerExtra?: React.ReactNode;
}

const ChecklistModal = ({
  title,
  items,
  groups,
  selectedIds,
  onConfirm,
  onClose,
  readOnly = false,
  headerExtra,
}: ChecklistModalProps) => {
  const [localSelected, setLocalSelected] = useState<number[]>([...selectedIds]);
  const [search, setSearch] = useState("");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() =>
    new Set(groups?.map((g) => g.groupLabel) || [])
  );
  const [categoryFilter, setCategoryFilter] = useState("");

  const isTreeMode = groups && groups.length > 0;

  // Flat mode filtering
  const filteredItems = useMemo(() => {
    if (isTreeMode) return [];
    if (!search.trim()) return items;
    const lower = search.toLowerCase();
    return items.filter((item) => item.label.toLowerCase().includes(lower));
  }, [items, search, isTreeMode]);

  // Tree mode filtering
  const filteredGroups = useMemo(() => {
    if (!isTreeMode) return [];
    let result = groups!;
    if (categoryFilter) {
      result = result.filter((g) => g.groupLabel === categoryFilter);
    }
    if (search.trim()) {
      const lower = search.toLowerCase();
      result = result
        .map((g) => ({
          ...g,
          items: g.items.filter(
            (item) =>
              item.label.toLowerCase().includes(lower) ||
              g.groupLabel.toLowerCase().includes(lower)
          ),
        }))
        .filter((g) => g.items.length > 0);
    }
    return result;
  }, [groups, search, categoryFilter, isTreeMode]);

  const allVisibleIds = useMemo(() => {
    if (isTreeMode) return filteredGroups.flatMap((g) => g.items.map((i) => i.id));
    return filteredItems.map((i) => i.id);
  }, [isTreeMode, filteredGroups, filteredItems]);

  const handleToggle = (id: number) => {
    setLocalSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleAll = () => {
    const allSelected = allVisibleIds.every((id) => localSelected.includes(id));
    if (allSelected) {
      setLocalSelected((prev) => prev.filter((id) => !allVisibleIds.includes(id)));
    } else {
      setLocalSelected((prev) => [...new Set([...prev, ...allVisibleIds])]);
    }
  };

  const handleToggleGroup = (group: ChecklistGroup) => {
    const groupIds = group.items.map((i) => i.id);
    const allSelected = groupIds.every((id) => localSelected.includes(id));
    if (allSelected) {
      setLocalSelected((prev) => prev.filter((id) => !groupIds.includes(id)));
    } else {
      setLocalSelected((prev) => [...new Set([...prev, ...groupIds])]);
    }
  };

  const toggleCollapse = (groupLabel: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupLabel)) next.delete(groupLabel);
      else next.add(groupLabel);
      return next;
    });
  };

  const isGroupFullySelected = (group: ChecklistGroup) =>
    group.items.length > 0 && group.items.every((i) => localSelected.includes(i.id));

  const isGroupPartiallySelected = (group: ChecklistGroup) =>
    group.items.some((i) => localSelected.includes(i.id)) && !isGroupFullySelected(group);

  const selectedCountInGroup = (group: ChecklistGroup) =>
    group.items.filter((i) => localSelected.includes(i.id)).length;

  const allFilteredSelected =
    allVisibleIds.length > 0 && allVisibleIds.every((id) => localSelected.includes(id));

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          {!readOnly && (
            <span className={styles.count}>
              {localSelected.length} seleccionados
            </span>
          )}
        </div>

        {headerExtra}

        <div className={styles.filters}>
          <input
            className={styles.search}
            type="text"
            placeholder="Buscar..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          {isTreeMode && (
            <div className={styles.categoryFilterWrapper}>
              <SelectInput
                label=""
                placeholder="Todas las categorías"
                value={categoryFilter}
                handleChange={(e: any) => setCategoryFilter(e.target.value)}
                items={[
                  { name: "Todas las categorías", value: "" },
                  ...groups!.map((g) => ({ name: g.groupLabel, value: g.groupLabel })),
                ]}
              />
            </div>
          )}
        </div>

        <div className={styles.list}>
          {!readOnly && allVisibleIds.length > 0 && (
            <label className={styles.selectAllRow}>
              <input
                type="checkbox"
                checked={allFilteredSelected}
                onChange={handleToggleAll}
              />
              <span>Seleccionar todos ({allVisibleIds.length})</span>
            </label>
          )}

          {/* Tree mode */}
          {isTreeMode &&
            filteredGroups.map((group) => {
              const isCollapsed = collapsedGroups.has(group.groupLabel);
              const full = isGroupFullySelected(group);
              const partial = isGroupPartiallySelected(group);
              const count = selectedCountInGroup(group);
              return (
                <div key={group.groupLabel} className={styles.group}>
                  <div className={styles.groupHeader}>
                    <button
                      type="button"
                      className={styles.collapseBtn}
                      onClick={() => toggleCollapse(group.groupLabel)}
                    >
                      {isCollapsed ? "▸" : "▾"}
                    </button>
                    <label className={styles.groupLabel}>
                      {!readOnly && (
                        <input
                          type="checkbox"
                          checked={full}
                          ref={(el) => { if (el) el.indeterminate = partial; }}
                          onChange={() => handleToggleGroup(group)}
                        />
                      )}
                      <span>{group.groupLabel}</span>
                      <span className={styles.groupCount}>
                        {readOnly ? `(${group.items.length})` : `(${count}/${group.items.length})`}
                      </span>
                    </label>
                  </div>
                  {!isCollapsed && (
                    <div className={styles.groupItems}>
                      {group.items.map((item) => (
                        <label key={item.id} className={styles.item}>
                          {!readOnly && (
                            <input
                              type="checkbox"
                              checked={localSelected.includes(item.id)}
                              onChange={() => handleToggle(item.id)}
                            />
                          )}
                          <span>{item.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

          {/* Flat mode */}
          {!isTreeMode &&
            filteredItems.map((item) => (
              <label key={item.id} className={styles.item}>
                {!readOnly && (
                  <input
                    type="checkbox"
                    checked={localSelected.includes(item.id)}
                    onChange={() => handleToggle(item.id)}
                  />
                )}
                <span>{item.label}</span>
              </label>
            ))}

          {allVisibleIds.length === 0 && (
            <p className={styles.empty}>No se encontraron resultados</p>
          )}
        </div>

        <div className={styles.actions}>
          {readOnly ? (
            <button className={styles.confirmBtn} onClick={onClose}>
              Cerrar
            </button>
          ) : (
            <>
              <button className={styles.cancelBtn} onClick={onClose}>
                Cancelar
              </button>
              <button
                className={styles.confirmBtn}
                onClick={() => onConfirm?.(localSelected)}
              >
                Confirmar
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistModal;
